import { Box, InputLabel, Stack, Typography } from '@mui/material'
import { Driver } from '../../../../store/drivers/drivers.type'
import { useMemo } from 'react'
import { DateTime } from 'luxon'
import { READ_ONLY_FIELD_BG } from '../../../../components/fields/field-read-only.styles'
import { getCitizenshipCountryLabel } from '../../data/citizenship-countries'

interface DriverLicenceProps {
  driver: Driver
}

const fieldRowSx = {
  display: 'grid',
  gridTemplateColumns: '168px minmax(0, 1fr)',
  columnGap: 2.5,
  alignItems: 'center',
  width: '100%',
} as const

const labelSx = {
  color: 'text.secondary',
  fontWeight: 500,
  fontSize: '0.875rem',
  lineHeight: 1.35,
  whiteSpace: 'normal',
} as const

const valueBoxSx = {
  backgroundColor: READ_ONLY_FIELD_BG,
  borderRadius: '8px',
  px: 1.5,
  py: 1.25,
  minHeight: 40,
  display: 'flex',
  alignItems: 'center',
} as const

const DetailRow = ({ label, value }: { label: string; value: string }) => (
  <Box sx={fieldRowSx}>
    <InputLabel sx={labelSx}>{label}</InputLabel>
    <Box sx={valueBoxSx}>
      <Typography
        sx={{
          fontSize: '0.875rem',
          lineHeight: 1.35,
          color: '#000000',
          wordBreak: 'break-word',
        }}
      >
        {value}
      </Typography>
    </Box>
  </Box>
)

const formatDate = (iso: string | null | undefined) => {
  if (!iso) return ''
  const dt = DateTime.fromISO(iso)
  return dt.isValid ? dt.toFormat('dd/MM/yyyy') : ''
}

const DriverLicence = ({ driver }: DriverLicenceProps) => {
  const fullName = useMemo(
    () => `${driver?.firstName ?? ''} ${driver?.lastName ?? ''}`.trim(),
    [driver]
  )

  const height = useMemo(() => {
    if (!driver?.height) return ''
    return `${driver.height} cm`
  }, [driver])

  return (
    <Box sx={{ width: '100%', maxWidth: 760, mt: 2 }}>
      <Stack spacing={2.5}>
        <DetailRow label="Name" value={fullName} />
        <DetailRow label="Address" value={driver?.address || ''} />
        <DetailRow label="Licence Number" value={driver?.licenseNumber || ''} />
        <DetailRow
          label="Date of Issue"
          value={formatDate(driver?.dateOfIssue)}
        />
        <DetailRow label="Expiry Date" value={formatDate(driver?.expiryDate)} />
        <DetailRow
          label="Driver Reference Number"
          value={driver?.driverReferenceNumber || ''}
        />
        <DetailRow label="Sex" value={driver?.sex || ''} />
        <DetailRow label="Licence Class" value={driver?.licenseClass || ''} />
        <DetailRow
          label="Restrictions/Conditions"
          value={driver?.restrictions || ''}
        />
        <DetailRow
          label="Date of Birth"
          value={formatDate(driver?.dateOfBirth)}
        />
        <DetailRow label="Height" value={height} />
        <DetailRow
          label="Type of License"
          value={driver?.typeOfLicense || ''}
        />
        <DetailRow
          label="Citizenship Country"
          value={getCitizenshipCountryLabel(driver?.citizenshipCountry)}
        />
        <DetailRow
          label="FAST Card Number"
          value={driver?.fastCardNumber || ''}
        />
      </Stack>
    </Box>
  )
}

export default DriverLicence
